/**
 * Payment proof rule shared by the admin verify flow.
 *
 * A payment counts as having proof when it carries the legacy `paymentProofUrl`
 * or at least one attachment with the "Proof" purpose. This mirrors the backend
 * rule in VerifyPaymentCommandHandler exactly — the payment DTOs return every
 * attachment ref of the payment, not only the proof ones, so the purpose filter
 * matters: without it the UI would allow a verification the API then rejects.
 */

const PROOF_PURPOSE = "proof";

interface PaymentProofSource {
  paymentProofUrl?: string;
  attachments?: { purpose?: string }[];
}

export const PAYMENT_PROOF_REQUIRED_MESSAGE =
  "This payment has no proof image attached. Ask the customer to upload the transfer proof, or have a SuperAdmin verify it.";

/**
 * Thrown by the verify mutation when a non-SuperAdmin tries to verify a payment
 * that has no proof attached.
 */
export class PaymentProofRequiredError extends Error {
  constructor() {
    super(PAYMENT_PROOF_REQUIRED_MESSAGE);
    this.name = "PaymentProofRequiredError";
  }
}

/**
 * Returns true when the payment has any proof file attached.
 */
export function hasPaymentProof(payment: PaymentProofSource): boolean {
  return (
    !!payment.paymentProofUrl?.trim() ||
    (payment.attachments ?? []).some(
      (a) => a.purpose?.toLowerCase() === PROOF_PURPOSE,
    )
  );
}

/**
 * Turns the API-relative `downloadUrl` of a proof into a path the API client can request.
 *
 * The backend deliberately returns the path without the version prefix, so every consumer
 * adds the same one place it lives.
 */
export function proofDownloadPath(downloadUrl: string): string {
  return `/api/v1${downloadUrl}`;
}

/**
 * Whether a proof file can be shown with an `<img>`.
 *
 * PDFs are a legal proof — the upload validator accepts them — so the caller must not assume
 * every proof is a picture. The content type decides when the backend knows it; otherwise the
 * extension does, and an unknown extension is treated as non-renderable rather than guessed.
 */
export function isRenderableImage(proof: {
  contentType?: string;
  fileName?: string;
}): boolean {
  if (proof.contentType) {
    return proof.contentType.toLowerCase().startsWith("image/");
  }

  return /\.(png|jpe?g|gif|webp)$/i.test(proof.fileName ?? "");
}

/**
 * Reads the file name out of a proof download path.
 *
 * The path is percent-encoded segment by segment on the way out of the API — proof files are
 * named after what the customer uploaded and routinely carry spaces — so the name has to be
 * decoded before it reaches a download attribute.
 */
export function proofFileNameOf(downloadUrl: string | undefined): string {
  const lastSegment = downloadUrl?.split("?")[0].split("/").pop();
  if (!lastSegment) return "payment-proof";

  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
}

/**
 * The proof files of a payment, in the shape the thumbnail and lightbox components take.
 *
 * The list endpoint already returns this as `proofs`; the DETAIL endpoint does not, and hands
 * back `attachments` plus a legacy `paymentProofUrl` instead. This applies the same rule the
 * server uses to build `proofs` (see `GetPaymentsQueryHandler.BuildProofs`): attachments when
 * there are any, otherwise the single legacy file, otherwise nothing — so the verify dialog
 * shows the same receipts whichever screen opened it.
 */
export function proofsOfDetail(payment: {
  attachments?: {
    attachmentId?: string;
    fileName?: string;
    contentType?: string;
    downloadUrl?: string;
  }[];
  paymentProofUrl?: string;
}): {
  attachmentId?: string;
  downloadUrl: string;
  fileName: string;
  contentType?: string;
}[] {
  const attachments = payment.attachments ?? [];

  if (attachments.length > 0) {
    return attachments
      .filter((a): a is typeof a & { downloadUrl: string } => !!a.downloadUrl)
      .map((a) => ({
        attachmentId: a.attachmentId,
        downloadUrl: a.downloadUrl,
        fileName: a.fileName || proofFileNameOf(a.downloadUrl),
        contentType: a.contentType,
      }));
  }

  const legacy = payment.paymentProofUrl?.trim();
  if (!legacy) {
    return [];
  }

  return [{ downloadUrl: legacy, fileName: proofFileNameOf(legacy) }];
}
